import React, {
    ComponentProps,
    ForwardedRef,
    forwardRef,
    useMemo
} from 'react';
import DOMPurify from 'dompurify';

export interface Props
    extends Omit<ComponentProps<'div'>, 'dangerouslySetInnerHTML'> {
    /** Raw HTML string to sanitize via DOMPurify and render; blank-target links automatically get rel="noopener noreferrer". */
    html?: string;
}

// known bug of DOMPurify in conjunction with onbeforeprint event. we can disregard it.
// https://github.com/cure53/DOMPurify/issues/529
const DOMPurifyErrorRegex =
    /Failed to execute '.*?' on '.*?': The provided callback is no longer runnable\./;

// dompurify 3.x is uninitialized in Node (no window) — guard before registering
if (typeof DOMPurify.addHook === 'function') {
    DOMPurify.addHook('afterSanitizeAttributes', node => {
        if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
            node.setAttribute('rel', 'noopener noreferrer');
        }
    });
}

function HBSanitized(
    { html, ...props }: Props,
    ref: ForwardedRef<HTMLDivElement>
) {
    const sanitizedHtml = useMemo(() => {
        if (!html || typeof DOMPurify.sanitize !== 'function') {
            return '';
        }
        try {
            return DOMPurify.sanitize(html, {
                ADD_ATTR: ['target', 'rel']
            });
        } catch (error) {
            if (DOMPurifyErrorRegex.test(error.message)) {
                return '';
            }
            throw error;
        }
    }, [html]);

    if (sanitizedHtml) {
        return (
            <div
                ref={ref}
                {...props}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
        );
    }
    return <div ref={ref} {...props} />;
}

export default forwardRef(HBSanitized);
